import { TreeCreationPersistor } from '../persistence/Persistor';
import { slugify } from './slugify';

export interface CreateTreeViewState {
    treeName: string;
    treeId: string;
    isPrivate: boolean;
    treeNameError: string | null;
    treeIdError: string | null;
    isSubmitEnabled: boolean;
    isSubmitting: boolean;
    error: string | null;
}

export class CreateTreeInteractor {
    private treeName: string = '';
    private treeId: string = '';
    private isPrivate: boolean = false;
    private treeIdManuallyEdited: boolean = false;
    private isSubmitting: boolean = false;
    private error: string | null = null;
    private treeIdError: string | null = null;
    private treeNameError: string | null = null;

    constructor(private readonly persistor: TreeCreationPersistor) {}

    setTreeName(name: string): void {
        this.treeName = name;
        this.error = null;
        this.treeNameError = null;

        if (!this.treeIdManuallyEdited) {
            this.treeId = slugify(name);
            this.treeIdError = null;
        }
    }

    setTreeId(id: string): void {
        this.treeId = id;
        this.treeIdManuallyEdited = true;
        this.error = null;
        this.treeIdError = null;
    }

    setIsPrivate(isPrivate: boolean): void {
        this.isPrivate = isPrivate;
        this.error = null;
    }

    getViewState(): CreateTreeViewState {
        const treeNameError = this.treeNameError ?? this.validateTreeName();
        const treeIdError = this.treeIdError ?? this.validateTreeId();
        const isSubmitEnabled = !treeNameError && !treeIdError && !this.isSubmitting;

        return {
            treeName: this.treeName,
            treeId: this.treeId,
            isPrivate: this.isPrivate,
            treeNameError,
            treeIdError,
            isSubmitEnabled,
            isSubmitting: this.isSubmitting,
            error: this.error
        };
    }

    async createTree(): Promise<boolean> {
        const treeNameError = this.validateTreeName();
        const treeIdError = this.validateTreeId();

        if (treeNameError || treeIdError) {
            return false;
        }

        this.isSubmitting = true;
        this.error = null;
        this.treeIdError = null;

        try {
            const result = await this.persistor.createTree(this.treeId.trim(), this.treeName.trim(), this.isPrivate);

            if (!result.success) {
                if (result.errorType === 'conflict') {
                    this.treeIdError = result.error;
                } else {
                    this.error = result.error;
                }
                return false;
            }

            return true;
        } catch (error) {
            this.error = error instanceof Error ? error.message : String(error);
            return false;
        } finally {
            this.isSubmitting = false;
        }
    }

    private validateTreeName(): string | null {
        const trimmed = this.treeName.trim();
        if (trimmed.length === 0) {
            return 'Tree name cannot be blank';
        }
        return null;
    }

    private validateTreeId(): string | null {
        const trimmed = this.treeId.trim();
        if (trimmed.length === 0) {
            return 'Tree ID cannot be blank';
        }

        const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
        if (!slugPattern.test(trimmed)) {
            return 'Tree ID must be a valid slug (lowercase letters, numbers, and hyphens only)';
        }

        return null;
    }
}

